/* eslint-disable no-unused-vars */
import {
    Form,
    Input,
    Typography,
    Row,
    Col,
    FormInstance,
} from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import { useTranslation } from 'react-i18next';

import { CSSProperties, FC, useState } from 'react';
import { phone } from 'phone';
import { IFieldProps } from './interface';

import { InputPhone } from './InputPhone';
import { CountryCodeSelector } from './CountryCodeSelector';
import { ECountryCodes } from '../../enums/country-code.enum';

const { Text } = Typography;

interface PhoneNumberProps {
    form: FormInstance;
    fieldProps: IFieldProps;
    defaultCountryCode?: string;
    phoneStyles?: CSSProperties;
    disabled?: boolean;
    handleSelect: (value: string) => void;
    onChangePhone?: (value: string) => void;
}

const PhoneNumber: FC<PhoneNumberProps> = ({
    form,
    fieldProps,
    handleSelect,
    defaultCountryCode,
    phoneStyles,
    disabled,
    onChangePhone,
}) => {
    const { t } = useTranslation('onboarding');

    const [phoneError, setPhoneError] = useState<string[]>([]);

    const handleOnChange = (value: string) => {
        setPhoneError(form.getFieldError(fieldProps.keyMobile as NamePath));
        if (onChangePhone) {
            onChangePhone(value);
        }
    };

    const validator = (_: any, value: string) => {
        const countryCode = form.getFieldValue(fieldProps.keyCountryCode as NamePath);

        let validateMobilePrefix = true;
        const invalidPrefix = [`+${ECountryCodes.GT}`, `+${ECountryCodes.CR}`, `+${ECountryCodes.CO}`];

        if (invalidPrefix.includes(countryCode)) {
            validateMobilePrefix = false;
        }

        const phoneNumber = `${countryCode}${value}`;

        const { isValid } = phone(phoneNumber, { validateMobilePrefix });

        if (!value) {
            setPhoneError([t('FIELD_REQUIRED')]);
            return Promise.reject(t('FIELD_REQUIRED'));
        }

        if (!isValid) {
            setPhoneError([t('PHONE_NUMBER_INVALID')]);
            return Promise.reject(t('PHONE_NUMBER_INVALID'));
        }

        setPhoneError([]);
        return Promise.resolve();
    };

    const handleCountryCode = (value: string) => {
        handleSelect(value);
        const mobileTouched = form.isFieldTouched(fieldProps.keyMobile as NamePath);
        if (mobileTouched) {
            (document.querySelector(`#${fieldProps.keyMobile}`) as HTMLInputElement).focus();
            form
                .validateFields([fieldProps.keyMobile as NamePath])
                .then(() => setPhoneError([]))
                .catch(() => setPhoneError(form.getFieldError(fieldProps.keyMobile as NamePath)));
        }
    };

    return (
        <Form.Item
            label={(
                <Text>{fieldProps.label}</Text>
            )}
            style={{ marginBottom: 0 }}
        >
            <Row style={{ width: '100%', display: 'flex' }}>
                <Col style={{ flex: 1 }}>
                    <CountryCodeSelector
                        defaultCountryCode={defaultCountryCode}
                        handleSelect={handleCountryCode}
                        disabled={disabled}
                    />
                    <Form.Item hidden name={fieldProps.keyCountryCode}>
                        <Input />
                    </Form.Item>
                </Col>
                <Col style={{ flex: 3, paddingLeft: 5 }}>
                    <Form.Item
                        name={fieldProps.keyMobile}
                        rules={[{ validator }]}
                        dependencies={[fieldProps.keyCountryCode as NamePath]}
                        style={phoneStyles}
                        validateTrigger={['onBlur', 'onChange']}
                    >
                        <InputPhone
                            onChange={handleOnChange}
                            phoneError={phoneError}
                            id={fieldProps.keyMobile}
                            disabled={disabled}
                            size="large"
                        />
                    </Form.Item>
                </Col>
            </Row>
        </Form.Item>
    );
};

export { PhoneNumber };
